import React, { useEffect, useMemo, useCallback } from 'react';
import chunk from 'lodash/chunk';
import { observer } from 'mobx-react';
import { ThemedAnswer } from '../themed/ThemedComponents';
import { useTranslation } from 'react-i18next';
import { FlexDiv } from '@awarego-ui/components';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import * as CONSTANTS from '../../utils/constants';

export default observer(
    ({ subject, question, answers, isAnswered, logEvent }) => {
        const { t } = useTranslation();
        const [chunks, setChunks] = React.useState(chunk(answers, 2));
        const [clicked, setClicked] = React.useState({});

        useEffect(() => {
            setChunks(chunk(answers, 2));
            if (isAnswered) {
                setClicked({});
            }
        }, [answers, isAnswered]);

        const answer = useCallback(
            (answer, position) => {
                if (!isAnswered && !clicked[position]) {
                    setClicked({ ...clicked, [position]: true });
                    if (answer.correct) {
                        logEvent(subject, CONSTANTS.EVENTS.ANSWARE_CORRECT);
                    } else {
                        logEvent(subject, CONSTANTS.EVENTS.ANSWARE_INCORRECT);
                    }
                }
            },
            [clicked, isAnswered, logEvent, subject]
        );

        const selectedPosition = Object.keys(clicked).pop();
        const selectedAnswer = useMemo(() => {
            if (!selectedPosition) return null;
            for (let i = 0; i < chunks.length; i++) {
                for (let j = 0; j < chunks[i].length; j++) {
                    if (`${i}-${j}` === selectedPosition) {
                        return chunks[i][j];
                    }
                }
            }
            return null;
        }, [chunks, selectedPosition]);

        const statusMessage = useMemo(() => {
            if (isAnswered) {
                return t('Answer selected');
            }
            if (selectedAnswer) {
                return selectedAnswer.correct
                    ? t('Correct answer selected')
                    : t('Incorrect answer selected');
            }
            return '';
        }, [isAnswered, selectedAnswer, t]);

        const questionId = 'quiz-question-text';

        const btns = useMemo(
            () =>
                chunks.map((x, i) => (
                    <FlexDiv
                        gap={16}
                        key={'col-' + i}
                        flexGrow={1}
                        justifyCenter
                        alignCenter
                    >
                        {x.map((a, j) => {
                            const position = `${i}-${j}`;
                            const isClicked = Boolean(clicked[position]);
                            const showCorrect =
                                a.correct && (isAnswered || isClicked);
                            const showIncorrect = !a.correct && isClicked;
                            const locked = isAnswered || isClicked;
                            return (
                                <ThemedAnswer
                                    type="button"
                                    aria-pressed={
                                        showCorrect || isClicked
                                            ? 'true'
                                            : 'false'
                                    }
                                    disabled={locked}
                                    correct={showCorrect}
                                    incorrect={showIncorrect}
                                    onClick={() => answer(a, position)}
                                    key={'ans-' + j}
                                >
                                    {a.text}
                                    {showCorrect && (
                                        <CheckIcon
                                            style={{
                                                color: 'white',
                                            }}
                                            fontSize="small"
                                            aria-hidden
                                        />
                                    )}
                                    {showIncorrect && (
                                        <CloseIcon
                                            style={{
                                                color: 'white',
                                            }}
                                            fontSize="small"
                                            aria-hidden
                                        />
                                    )}
                                </ThemedAnswer>
                            );
                        })}
                    </FlexDiv>
                )),
            [answer, chunks, clicked, isAnswered]
        );

        if (!question) {
            return null;
        } else {
            return (
                <div className="section-exam">
                    <div className="exam-question-holder">
                        {/* Todo add question counter*/}
                        <div className="exam-question-counter">
                            {t('Question')} 1/1
                        </div>
                        <div className="exam-question" id={questionId}>
                            {question}
                        </div>
                        <div
                            role="group"
                            aria-labelledby={questionId}
                        >
                            <FlexDiv
                                gap={16}
                                fullWidth
                                justifyCenter
                                desktopOnly
                            >
                                {btns}
                            </FlexDiv>
                            <FlexDiv gap={16} fullWidth column mobileOnly>
                                {btns}
                            </FlexDiv>
                        </div>
                        <div
                            role="status"
                            aria-live="polite"
                            className="sr-only"
                        >
                            {statusMessage}
                        </div>
                    </div>
                </div>
            );
        }
    }
);
